import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import axiosInstance from 'api/axiosInstance'
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  isTokenExpired,
  saveTokens
} from '../utils'
import { IApiResponse, LoginResponse, MeResponse } from 'api/types'
import {
  useClearSelectedProjectId,
  useSelectedProjectId,
  useSetSelectedProjectId
} from 'api/projectSelectionApi'
import { useAppDispatch } from 'store/hooks'
import { clearAllApiCaches } from 'store/clearApiCaches'

interface AuthContextType {
  isLoggedIn: boolean;
  user: MeResponse | null;
  token: string | null;
  /** Convenience — first role on the user, or '' if none. */
  role: string;
  /** Set of roles, prefixed with `ROLE_` to align with BE Spring Security style. */
  roles: string[];
  /** Effective permission codes (`project.read`, `lead.assign`, …). */
  permissions: string[];
  /** Module codes currently enabled in `module_config` (`PROJECT`, `LEAD`, …). */
  modules: string[];
  /** True if the user has the given permission code. */
  hasPermission: (code: string) => boolean;
  /** True if the user has any of the given permission codes. */
  hasAnyPermission: (codes: string[]) => boolean;
  /** True if the named module is currently enabled. */
  isModuleEnabled: (code: string) => boolean;
  loading: boolean;
  /** Persist tokens, hydrate user. */
  loginWithTokens: (resp: LoginResponse) => Promise<MeResponse | null>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  /**
   * Globally-selected project ID — drives the `X-Project-Id` header that
   * axiosBaseQuery attaches on project-scoped requests. Null when the user
   * has not yet picked one.
   */
  projectId: string | null;
  /**
   * Set the selected project. Persists to localStorage and wipes every RTK
   * Query cache so the next render refetches under the new scope. Pass
   * `null` to clear the selection.
   */
  setProjectId: (id: string | null) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Module-level promise deduplication — prevents React StrictMode's double-invoke
// from firing two concurrent /auth/me requests.
let inFlightMe: Promise<MeResponse | null> | null = null

const fetchMe = (): Promise<MeResponse | null> => {
  if (inFlightMe) return inFlightMe
  inFlightMe = axiosInstance
    .get<IApiResponse<MeResponse>>('/auth/me')
    .then((resp) => resp.data?.data ?? null)
    .catch(() => null)
    .finally(() => { inFlightMe = null })
  return inFlightMe
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [user, setUser] = useState<MeResponse | null>(null)
  const [token, setToken] = useState<string | null>(getAccessToken())
  const [loading, setLoading] = useState<boolean>(!!getAccessToken())
  // Selected project lives in its own api/projectSelectionApi slice.
  // AuthContext just bridges the value + setter through so existing
  // `useAuth().projectId` call sites keep working unchanged.
  const projectId = useSelectedProjectId()
  const setProjectId = useSetSelectedProjectId()
  const clearSelectedProject = useClearSelectedProjectId()
  const dispatch = useAppDispatch()

  const handleLogout = useCallback(() => {
    clearTokens()
    setToken(null)
    setUser(null)
    setLoading(false)
    // Clear the project selection on logout — different users may have
    // access to different projects.
    clearSelectedProject()
    // Clear every RTK Query cache so the next user that logs in on this
    // session gets fresh, correctly-scoped data instead of the previous
    // user's cached projects / bookings / etc. Runs only on logout; it
    // doesn't touch any other state or the logged-in experience.
    clearAllApiCaches(dispatch)
  }, [clearSelectedProject, dispatch])

  const refreshUser = useCallback(async () => {
    const me = await fetchMe()
    setUser(me)
  }, [])

  // On mount: hydrate the user from /auth/me only if we have a token that
  // could plausibly succeed. Skip the network round-trip when both tokens are
  // missing or locally expired — otherwise we burn a guaranteed-fail /me +
  // /refresh pair on every cold load for a logged-out (or stale-storage) user.
  useEffect(() => {
    const access = getAccessToken()
    const refresh = getRefreshToken()

    const accessUsable = !!access && !isTokenExpired(access)
    const refreshUsable = !!refresh && !isTokenExpired(refresh)

    if (!accessUsable && !refreshUsable) {
      handleLogout()
      return
    }

    setLoading(true)
    fetchMe()
      .then((me) => {
        if (me) {
          setUser(me)
          setToken(getAccessToken())
        } else {
          handleLogout()
        }
      })
      .finally(() => setLoading(false))
  }, [handleLogout])

  const loginWithTokens = useCallback(
    async (resp: LoginResponse): Promise<MeResponse | null> => {
      // A `mustEnrollTwoFactor` login carries an enrolment token instead of a
      // session. Callers must branch on that before getting here.
      if (!resp.accessToken) {
        throw new Error('loginWithTokens called without an access token')
      }
      saveTokens(resp.accessToken, resp.refreshToken ?? '')
      setToken(resp.accessToken)
      const me = await fetchMe()
      setUser(me)
      return me
    },
    []
  )

  const roles = useMemo(
    () => (user?.roles ?? []).map((r) => (r.startsWith('ROLE_') ? r : `ROLE_${r}`)),
    [user]
  )

  const permissions = useMemo(() => user?.permissions ?? [], [user])
  const permissionSet = useMemo(() => new Set(permissions), [permissions])
  const hasPermission = useCallback(
    (code: string) => permissionSet.has(code),
    [permissionSet]
  )
  const hasAnyPermission = useCallback(
    (codes: string[]) => codes.some((c) => permissionSet.has(c)),
    [permissionSet]
  )

  const modules = useMemo(() => user?.modules ?? [], [user])
  const moduleSet = useMemo(() => new Set(modules), [modules])
  const isModuleEnabled = useCallback(
    (code: string) => moduleSet.has(code),
    [moduleSet]
  )

  const value = useMemo<AuthContextType>(
    () => ({
      isLoggedIn: !!user && !!token,
      user,
      token,
      role: roles[0] ?? '',
      roles,
      permissions,
      modules,
      hasPermission,
      hasAnyPermission,
      isModuleEnabled,
      loading,
      loginWithTokens,
      logout: handleLogout,
      refreshUser,
      projectId,
      setProjectId
    }),
    [
      user,
      token,
      roles,
      permissions,
      modules,
      hasPermission,
      hasAnyPermission,
      isModuleEnabled,
      loading,
      loginWithTokens,
      handleLogout,
      refreshUser,
      projectId,
      setProjectId
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
